import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  LayoutDashboard,
  MessageSquare,
  Calendar,
  CalendarDays,
  Clock,
  FileText,
  ClipboardList,
  Star,
  Settings,
  LogOut,
  User,
  Mail,
} from 'lucide-react';

interface AdminLayoutProps {
  children: ReactNode;
}

const navGroups = [
  {
    label: null,
    color: 'violet',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' }
    ]
  },
  {
    label: 'Client Management',
    color: 'green',
    items: [
      { label: 'Clients', icon: User, path: '/admin/clients' },
      { label: 'Documents', icon: FileText, path: '/admin/documents' }
    ]
  },
  {
    label: 'Scheduling',
    color: 'orange',
    items: [
      { label: 'Appointments', icon: Calendar, path: '/admin/scheduling' },
      { label: 'Calendar', icon: CalendarDays, path: '/admin/scheduling/calendar' },
      { label: 'Availability', icon: Clock, path: '/admin/availability' }
    ]
  },
  {
    label: 'Communication',
    color: 'blue',
    items: [
      { label: 'Chat', icon: MessageSquare, path: '/admin/chat' },
      { label: 'Email', icon: Mail, path: '/admin/email' }
    ]
  },
  {
    label: null,
    color: 'slate',
    items: [
      { label: 'Settings', icon: Settings, path: '/admin/settings' }
    ]
  }
];

function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  const getNavCls = ({ isActive }: { isActive: boolean }, color: string) => {
    const baseClasses = 'transition-all duration-200';
    const colorMap: Record<string, string> = {
      violet: isActive ? 'bg-violet-500/10 text-violet-600 border-l-2 border-violet-500' : 'hover:bg-violet-500/5',
      green: isActive ? 'bg-green-500/10 text-green-600 border-l-2 border-green-500' : 'hover:bg-green-500/5',
      orange: isActive ? 'bg-orange-500/10 text-orange-600 border-l-2 border-orange-500' : 'hover:bg-orange-500/5',
      blue: isActive ? 'bg-blue-500/10 text-blue-600 border-l-2 border-blue-500' : 'hover:bg-blue-500/5',
      slate: isActive ? 'bg-slate-500/10 text-slate-600 border-l-2 border-slate-500' : 'hover:bg-slate-500/5',
    };
    return `${baseClasses} ${colorMap[color] || ''}`;
  };

  const getIconColor = (color: string) => {
    const colorMap: Record<string, string> = {
      violet: 'text-violet-500',
      green: 'text-green-500',
      orange: 'text-orange-500',
      blue: 'text-blue-500',
      slate: 'text-slate-500',
    };
    return colorMap[color] || 'text-muted-foreground';
  };

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-60'} collapsible="icon">
      <SidebarHeader className="border-b p-4">
        {collapsed ? (
          <img 
            src="/images/logo-tiny-tax-lady.png" 
            alt="Tiny Tax Lady" 
            className="h-8 w-8 rounded-lg shadow-sm"
          />
        ) : (
          <div className="flex items-center gap-3">
            <img 
              src="/images/logo-tiny-tax-lady.png" 
              alt="Tiny Tax Lady" 
              className="h-10 w-10 rounded-lg shadow-sm"
            />
            <div className="flex flex-col">
              <span className="font-semibold text-sm">The Tiny Tax Lady</span>
              <span className="text-xs text-muted-foreground">Admin Portal</span>
            </div>
          </div>
        )}
      </SidebarHeader>
      
      <SidebarContent>
        {navGroups.map((group, groupIndex) => (
          <SidebarGroup key={groupIndex} className={group.label ? 'mt-4' : ''}>
            {group.label && (
              <SidebarGroupLabel className={`${getIconColor(group.color)} font-semibold`}>
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton asChild>
                      <NavLink 
                        to={item.path} 
                        end 
                        className={({ isActive }) => getNavCls({ isActive }, group.color)}
                      >
                        <item.icon className={`h-4 w-4 ${getIconColor(group.color)}`} />
                        {!collapsed && <span className="ml-2">{item.label}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, signOut } = useAuth();

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />

        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="h-16 border-b bg-card flex items-center justify-between px-6 sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <SidebarTrigger />
              <div className="flex items-center gap-3">
                <img 
                  src="/images/logo-tiny-tax-lady.png" 
                  alt="Tiny Tax Lady" 
                  className="h-8 w-8 rounded-lg shadow-sm"
                />
                <h1 className="text-lg font-semibold">The Tiny Tax Lady - Admin</h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{user?.email}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 p-6 admin-background">
            {children}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
